import LauncherResources from './LauncherResources';

export default class LauncherResourcesJson extends LauncherResources {
    constructor(options, features) {
        super();

        if (options) {
            if (options instanceof Promise) {
                this.optionsPromise = options;
            } else {
                this.options = options;
            }
        } else {
            throw new Error("Either options or options path must be provided");
        }

        if (features) {
            if (features instanceof Promise) {
                this.featuresPromise = features;
            } else {
                this.features = features;
            }
        } else {
            throw new Error("Either features or features path must be provided");
        }
    }

    async getOptions() {
        return this.options || await this.optionsPromise;
    }

    async getFeaturesObject() {
        return this.features || await this.featuresPromise;
    }

    loadOptions(jsonURL) {
        const headers = new Headers({'Content-Type': 'application/json'});
        return fetch(jsonURL, { method: 'GET', headers })
            .then(v => v.json());
    }

    processOptions(optionsObject, {
        optionName,
        optionsKey = "options",
        defaultOptionKey = "defaultOption",
        labelKey = "label",
        valueKey = "value",
        multiValue = false
    }) {
        let options;
        // Check if options are available by key, then check if it is an array
        if (optionsObject[optionsKey]) {
            options = optionsObject[optionsKey];
        } else if (optionsObject instanceof Array) {
            options = optionsObject;
        } else {
            throw Error(`Could not find options by key ${optionsKey}`);
        }

        // Get the label and values by key
        options = options.map(o => ({ label: o[labelKey], value: o[valueKey] }));

        let defaultValue;
        if (optionName && this._getDefaultValueForOption(optionName)) {
            defaultValue = this._getDefaultValueForOption(optionName);
        } else if (multiValue) {
            defaultValue = {};
        } else if (optionsObject[defaultOptionKey]) {
            defaultValue = optionsObject[defaultOptionKey][valueKey];
        } else {
            defaultValue = options[0][valueKey];
        }

        return { options, defaultValue };
    }

    /*
     * Overridden method
     */
    async getOptionValues(option) {
        let allOptions;
        if (option !== LauncherResources.FEATURES_OPTION) {
            allOptions = await this.getOptions();
        }

        switch (option) {
            case LauncherResources.TYPE_OPTION:
            case LauncherResources.BUILD_TOOL_OPTION:
            case LauncherResources.TEST_OPTION:
            case LauncherResources.JAVA_VERSION_OPTION:
            case LauncherResources.LANGUAGE_OPTION:
                return this.processOptions(allOptions[option], {optionName: option});
            case LauncherResources.MICRONAUT_VERSION_OPTION:
                const micronautVersion = allOptions.micronautVersion;
                const micronautVersionOptions = [{ value: micronautVersion, label: micronautVersion }];
                return { options: micronautVersionOptions, defaultValue: micronautVersion }
            case LauncherResources.APPLICATION_VERSION_OPTION:
                const version = allOptions.version;
                const versionOptions = [{ value: version, label: version }];
                return { options: versionOptions, defaultValue: version }
            case LauncherResources.CLOUDS_OPTION:
                return this.processOptions(allOptions[option], {optionName: option, multiValue: true});
            case LauncherResources.SERVICES_OPTION:
                return this.getServices(allOptions);
            case LauncherResources.FEATURES_OPTION:
                return await this.getFeatures();
            default:
                throw Error(`Option ${option} not supported`);
        };
    }

    getServices(allOptions) {
        const services = allOptions.services.options;
        return {
            options: services.map(o => ({
                name: o.value,
                title: o.label,
                description: o.description,
                documentationModuleName: o.documentationModuleName
            })),
            defaultValue: this._getDefaultValueForOption(LauncherResources.SERVICES_OPTION) || {}
        };
    }

    async getFeatures(applicationType) {
        const features = await this.getFeaturesObject();
        return {
            options: features,
            defaultValue: this._getDefaultValueForOption(LauncherResources.FEATURES_OPTION) || {}
        };
    }
}
